import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Button,
    // Switch,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    IconButton,
    Avatar
} from '@mui/material';
import { Edit, /* Delete, */ Add, PhotoCamera } from '@mui/icons-material';
import { uploadImagecertifates } from '../../Redux/Actions/Action';
import { validateFile } from '../../utils/comman-function';

const HomeNavIcons = () => {
    const dispatch = useDispatch();
    const [icons, setIcons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalDocs, setTotalDocs] = useState(0);

    // Dialog state
    const [openModal, setOpenModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        iconImage: '',
        order: '',
        isActive: true
    });
    const [imageUploading, setImageUploading] = useState(false);

    // Delete confirmation state
    const [deleteId, setDeleteId] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const token = localStorage.getItem('astroVani-admin-token');
    const baseUrl = process.env.REACT_APP_BASEURL;

    const fetchIcons = async (currentPage = page, currentLimit = pageSize) => {
        setLoading(true);
        try {
            const res = await axios.get(`${baseUrl}/admin/nav-icons?page=${currentPage}&limit=${currentLimit}`, {
                headers: { Authorization: token }
            });
            if (res?.data?.success) {
                setIcons(res.data.data || []);
                setTotalDocs(res.data.paginationDetail?.totalDocs || 0);
            }
        } catch (error) {
            console.error('Error fetching nav icons:', error);
            toast.error('Failed to fetch Home Nav Icons');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIcons(page, pageSize);
    }, [page, pageSize]);

    const handleOpenModal = (icon = null) => {
        if (!icon && totalDocs >= 5) {
            toast.warning('You can only add up to 5 Nav Icons.');
            return;
        }

        if (icon) {
            setEditId(icon._id);
            setFormData({
                name: icon.name || '',
                iconImage: icon.iconImage || '',
                order: icon.order || '',
                isActive: icon.isActive ?? true
            });
        } else {
            setEditId(null);
            setFormData({
                name: '',
                iconImage: '',
                order: '',
                isActive: true
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditId(null);
        setFormData({ name: '', iconImage: '', order: '', isActive: true });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (file.type !== 'image/png' || !file.name.toLowerCase().endsWith('.png')) {
            toast.error('Please select a valid PNG image file (.png).');
            event.target.value = '';
            return;
        }
        if (!validateFile(file, 'image')) {
            event.target.value = '';
            return;
        }

        setImageUploading(true);
        try {
            const uploadedUrl = await dispatch(uploadImagecertifates(file));
            if (uploadedUrl) {
                setFormData(prev => ({ ...prev, iconImage: uploadedUrl.toString() }));
                toast.success('Image uploaded!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Error uploading image.');
        } finally {
            setImageUploading(false);
            event.target.value = ''; // Reset input
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim() || !formData.iconImage /* || formData.order === '' */) {
            toast.error('Please fill all required fields (Name, Image).');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: formData.name,
                iconImage: formData.iconImage,
                ...(formData.order !== '' && formData.order !== undefined ? { order: Number(formData.order) } : {}),
                isActive: formData.isActive
            };

            if (editId) {
                await axios.patch(`${baseUrl}/admin/nav-icons/${editId}`, payload, {
                    headers: { Authorization: token, 'Content-Type': 'application/json' }
                });
                toast.success('Nav Icon updated successfully');
            } else {
                await axios.post(`${baseUrl}/admin/nav-icons`, payload, {
                    headers: { Authorization: token, 'Content-Type': 'application/json' }
                });
                toast.success('Nav Icon created successfully');
            }
            handleCloseModal();
            fetchIcons();
        } catch (error) {
            console.error('Save error:', error);
            toast.error(error?.response?.data?.message || 'Failed to save Nav Icon');
        } finally {
            setSaving(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const confirmDelete = (id) => {
        setDeleteId(id);
        setOpenDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await axios.delete(`${baseUrl}/admin/nav-icons/${deleteId}`, {
                headers: { Authorization: token }
            });
            toast.success('Nav Icon deleted successfully');
            fetchIcons();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(error?.response?.data?.message || 'Failed to delete Nav Icon');
        } finally {
            setOpenDeleteModal(false);
            setDeleteId(null);
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            await axios.patch(`${baseUrl}/admin/nav-icons/toggleStatus/${id}`, {}, {
                headers: { Authorization: token }
            });
            toast.success('Status updated successfully');
            fetchIcons();
        } catch (error) {
            console.error('Toggle status error:', error);
            toast.error(error?.response?.data?.message || 'Failed to update status');
        }
    };

    const columns = [
        { field: 'order', headerName: 'Order', width: 100, align: 'center', headerAlign: 'center' },
        {
            field: 'iconImage',
            headerName: 'Icon Image',
            width: 120,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Avatar src={params.value} variant="rounded" sx={{ width: 40, height: 40, bgcolor: '#f0f0f0' }} />
                </Box>
            )
        },
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
        // {
        //     field: 'isActive',
        //     headerName: 'Status',
        //     width: 120,
        //     renderCell: (params) => (
        //         <Switch
        //             checked={params.value}
        //             color="success"
        //             onChange={() => handleToggleStatus(params.row._id)}
        //         />
        //     )
        // },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params) => (
                <Box>
                    <IconButton color="primary" onClick={() => handleOpenModal(params.row)}>
                        <Edit />
                    </IconButton>
                    {/* <IconButton color="error" onClick={() => confirmDelete(params.row._id)}>
                        <Delete />
                    </IconButton> */}
                </Box>
            )
        }
    ];

    return (
        <div className="container-xxl">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={2}>
                <Typography variant="h5" fontWeight="bold">Home Nav Icons</Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<Add />} 
                    onClick={() => handleOpenModal()} 
                    disabled={totalDocs >= 5}
                >
                    Add New Icon
                </Button>
            </Box>

            <Box sx={{ height: 650, width: '100%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <DataGrid
                    rows={icons}
                    columns={columns}
                    getRowId={(row) => row._id}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalDocs}
                    page={page - 1}
                    pageSizeOptions={[10, 20, 50]}
                    paginationModel={{ page: page - 1, pageSize }}
                    onPaginationModelChange={(newModel) => {
                        setPage(newModel.page + 1);
                        setPageSize(newModel.pageSize);
                    }}
                    disableRowSelectionOnClick
                />
            </Box>

            <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle>{editId ? 'Edit Nav Icon' : 'Add New Nav Icon'}</DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" gap={3}>
                        <TextField
                            label="Icon Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g. Pooja, Chat"
                        />
                        
                        {/* <TextField
                            label="Display Order"
                            type="number"
                            fullWidth
                            value={formData.order}
                            onChange={(e) => setFormData(prev => ({ ...prev, order: e.target.value }))}
                            placeholder="e.g. 1"
                            inputProps={{ min: 1 }}
                        /> */}

                        <Box>
                            <Typography variant="subtitle2" color="textSecondary" mb={1}>Icon Image</Typography>
                            <Box display="flex" alignItems="center" gap={2}>
                                {formData.iconImage ? (
                                    <Box position="relative">
                                        <img src={formData.iconImage} alt="Icon Preview" style={{ width: 80, height: 80, objectFit: 'contain', border: '1px solid #ccc', borderRadius: 8 }} />
                                    </Box>
                                ) : (
                                    <Box width={80} height={80} display="flex" alignItems="center" justifyContent="center" bgcolor="#f5f5f5" border="1px dashed #ccc" borderRadius={2}>
                                        <PhotoCamera color="disabled" />
                                    </Box>
                                )}
                                <Button
                                    variant="outlined"
                                    component="label"
                                    disabled={imageUploading}
                                    startIcon={imageUploading && <CircularProgress size={16} />}
                                >
                                    {imageUploading ? 'Uploading...' : 'Upload Image'}
                                    <input type="file" hidden accept="image/png" onChange={handleImageUpload} />
                                </Button>
                            </Box>
                        </Box>

                        {/* <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography>Status (Active)</Typography>
                            <Switch
                                checked={formData.isActive}
                                color="success"
                                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                            />
                        </Box> */}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving || imageUploading}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this Nav Icon? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteModal(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default HomeNavIcons;
